import { useCallback, useState } from 'react'

import { useToast } from '../../../hooks/useToast'
import type { Invoice } from '../types/invoice'
import { formatPeriodLabel } from '../utils/formatCents'
import { useUpdateInvoiceMutation } from './useUpdateInvoiceMutation'

function splitPeriodForEdit(invoice: Invoice): { start: string; end: string } {
  // backend returns ISO datetime ('2026-06-01T00:00:00.000Z'); date input
  // only accepts 'YYYY-MM-DD'. Slice the date portion either way.
  return {
    start: (invoice.period.startDate ?? '').slice(0, 10),
    end: (invoice.period.endDate ?? '').slice(0, 10),
  }
}

export function useInvoiceEditHandlers(dealId: string) {
  const toast = useToast()
  const updateMutation = useUpdateInvoiceMutation()

  const [editingPeriodFor, setEditingPeriodFor] = useState<string | null>(null)
  const [periodDraft, setPeriodDraft] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  })
  const [editingQuoteFor, setEditingQuoteFor] = useState<string | null>(null)
  const [quoteDraft, setQuoteDraft] = useState('')

  const startEditPeriod = useCallback((invoice: Invoice): void => {
    setPeriodDraft(splitPeriodForEdit(invoice))
    setEditingPeriodFor(invoice.invoiceNumber)
  }, [])

  const cancelEditPeriod = useCallback((): void => {
    setEditingPeriodFor(null)
    setPeriodDraft({ start: '', end: '' })
  }, [])

  const saveEditPeriod = useCallback(
    (invoice: Invoice): void => {
      const startDate = periodDraft.start
      const endDate = periodDraft.end
      if (!startDate || !endDate) {
        toast.current?.show({
          severity: 'error',
          summary: 'Invalid period',
          detail: 'Please choose both start and end dates.',
          sticky: true,
        })
        return
      }
      const label = formatPeriodLabel(startDate, endDate)
      const period = { startDate, endDate, label }
      updateMutation.mutate({
        dealId,
        invoiceId: invoice.id,
        patch: { period },
        body: { period },
      })
      setEditingPeriodFor(null)
    },
    [dealId, periodDraft.start, periodDraft.end, toast, updateMutation],
  )

  const startEditQuote = useCallback((invoice: Invoice): void => {
    setQuoteDraft(invoice.quoteNumber ?? '')
    setEditingQuoteFor(invoice.invoiceNumber)
  }, [])

  const cancelEditQuote = useCallback((): void => {
    setEditingQuoteFor(null)
    setQuoteDraft('')
  }, [])

  const saveEditQuote = useCallback(
    (invoice: Invoice): void => {
      const next = quoteDraft.trim()
      updateMutation.mutate({
        dealId,
        invoiceId: invoice.id,
        patch: { quoteNumber: next || undefined },
        body: { quoteNumber: next },
      })
      setEditingQuoteFor(null)
    },
    [dealId, quoteDraft, updateMutation],
  )

  return {
    editingPeriodFor,
    periodDraft,
    setPeriodDraft,
    editingQuoteFor,
    quoteDraft,
    setQuoteDraft,
    startEditPeriod,
    cancelEditPeriod,
    saveEditPeriod,
    startEditQuote,
    cancelEditQuote,
    saveEditQuote,
  }
}
